import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Dropdown.scss";
interface DropdownProps {
  open: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement | Element | null>;
  onClose: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  offsetY?: number;
  offsetX?: number;
}

export const Dropdown: React.FC<DropdownProps> = ({
  open,
  anchorRef,
  onClose,
  children,
  className = "",
  offsetY = 0,
  offsetX = 0,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    anchorCenterX: number;
    anchorRight: number;
    width: number;
  }>({ top: 0, anchorCenterX: 0, anchorRight: 0, width: 0 });

  useEffect(() => {
    if (open && anchorRef && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;

      setPosition({
        top: rect.bottom + window.scrollY + 8, // Add scrollY to account for page scroll
        anchorCenterX: centerX, // Center align with the anchor element
        anchorRight: rect.right,
        width: rect.width,
      });
    }
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        (!anchorRef ||
          !anchorRef.current ||
          !anchorRef.current.contains(event.target as Node))
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open) return;

    // Update position function
    function updatePosition() {
      if (anchorRef && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;

        setPosition({
          top: rect.bottom + window.scrollY + 14, // Add scrollY to account for page scroll
          anchorCenterX: centerX, // Center align with the anchor element
          anchorRight: rect.right,
          width: rect.width,
        });
      }
    }

    // Initialize ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    // Observe both window and anchor element for size changes
    if (anchorRef && anchorRef.current) {
      resizeObserver.observe(anchorRef.current);
    }

    // Call immediately on mount
    updatePosition();

    // Also listen to window resize
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      if (anchorRef && anchorRef.current) {
        resizeObserver.unobserve(anchorRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [open, anchorRef]);

  if (!open) return null;

  // Determine if this is the users dropdown
  const isUsersDropdown = className.includes("users-dropdown");
  const isMessageDropdown = className.includes("dropdown-menu--message");

  const computedLeft = isUsersDropdown
    ? position.anchorCenterX
    : isMessageDropdown
    ? position.anchorRight + 16
    : position.anchorCenterX;

  const computedTransform = isUsersDropdown
    ? "none"
    : isMessageDropdown
    ? "translateX(-100%)"
    : "translateX(-50%)";

  return createPortal(
    <div
      ref={dropdownRef}
      className={`dropdown ${className}`}
      style={{
        position: "fixed", // Use fixed positioning so it stays in place during scroll
        top: position.top + offsetY,
        left: computedLeft + offsetX,
        transform: computedTransform,
        width: isUsersDropdown ? "20rem" : undefined,
      }}
    >
      {children}
    </div>,
    document.body
  );
};
